/**
 * Centralized API Client
 * Handles JWT token injection, error handling, automatic token refresh, and all HTTP requests
 */

import { config } from './env';

export interface ApiError {
  message: string;
  status?: number;
  errors?: Record<string, string[]>;
}

class ApiClient {
  private baseUrl: string;
  private _isRefreshing: boolean = false;
  private _refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Attempt to refresh the access token using the stored refresh token.
   * Returns true if successful, false otherwise.
   * Uses a shared promise to prevent concurrent refresh calls.
   */
  private async tryRefreshToken(): Promise<boolean> {
    // If already refreshing, wait for that to finish
    if (this._isRefreshing && this._refreshPromise) {
      return this._refreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    this._isRefreshing = true;
    this._refreshPromise = (async () => {
      try {
        const params = new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: config.keycloak.clientId,
          refresh_token: refreshToken,
        });

        const response = await fetch(config.keycloak.tokenUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        });

        if (!response.ok) {
          console.warn('[ApiClient] Token refresh failed:', response.status);
          return false;
        }

        const data = await response.json();
        if (!data || !data.access_token) return false;

        localStorage.setItem('accessToken', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refreshToken', data.refresh_token);
        }

        // Dispatch event so AuthContext can update its state
        window.dispatchEvent(new CustomEvent('token-refreshed', { detail: data }));
        return true;
      } catch (err) {
        console.error('[ApiClient] Token refresh error:', err);
        return false;
      } finally {
        this._isRefreshing = false;
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  private async handleResponse(response: Response): Promise<any> {
    if (response.status === 204) {
      return null;
    }

    const contentType = response.headers.get('content-type') || '';
    let data;

    if (contentType.includes('application/json')) {
      try {
        data = await response.json();
      } catch (e) {
        data = await response.text();
      }
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // For 401, we DON'T handle it here — it's handled in request() with retry logic
      const errorMsg =
        (typeof data === 'object' && data !== null
          ? data.error || data.message || (data.errors ? Object.values(data.errors).join(', ') : null)
          : null) ||
        (typeof data === 'string' && data.length > 0 ? data : null) ||
        `HTTP ${response.status}`;

      const error: ApiError = {
        message: errorMsg,
        status: response.status,
        details: typeof data === 'object' ? data : undefined,
      };

      throw error;
    }

    return data;
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit & { params?: Record<string, any> } = {},
    retryCount: number = 0
  ): Promise<T> {
    const { params, ...fetchOptions } = options;
    const method = fetchOptions.method || 'GET';

    let url = `${this.baseUrl}${endpoint}`;

    if (params) {
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach((val) => {
              if (val !== null && val !== undefined) {
                queryString.append(key, String(val));
              }
            });
          } else {
            queryString.append(key, String(value));
          }
        }
      });
      if (queryString.toString()) {
        url += `?${queryString.toString()}`;
      }
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers: this.getHeaders(),
      });

      // On 401: try refresh + retry once
      if (response.status === 401) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Retry with new token
          const retryResponse = await fetch(url, {
            ...fetchOptions,
            headers: this.getHeaders(),
          });
          return this.handleResponse(retryResponse);
        }

        // Refresh failed — clear session
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.dispatchEvent(new Event('auth-session-expired'));

        const error: ApiError = {
          message: 'Session expired. Please log in again.',
          status: 401,
        };
        throw error;
      }

      // Retry on 429 (rate limited) — GET only, max 2 retries with longer backoff
      if (response.status === 429 && method === 'GET' && retryCount < 2) {
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : Math.pow(2, retryCount + 2) * 2500;
        console.warn(`[ApiClient] 429 on GET ${endpoint}, backing off ${delay}ms (attempt ${retryCount + 1}/2)`);
        await this.sleep(delay);
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      // Task 18: Retry on 503, 504 (GET only, max 3 retries)
      if ((response.status === 503 || response.status === 504) && method === 'GET' && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.warn(`[ApiClient] ${response.status} on GET ${endpoint}, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
        await this.sleep(delay);
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      return this.handleResponse(response);
    } catch (err: any) {
      // Task 18: Retry on network errors (GET only, max 3 retries)
      // "Failed to fetch" is the standard message for network errors in browser fetch
      const isNetworkError = err instanceof TypeError && (err.message === 'Failed to fetch' || err.message.includes('network error'));
      if (isNetworkError && method === 'GET' && retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        console.warn(`[ApiClient] Network error on GET ${endpoint}, retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
        await this.sleep(delay);
        return this.request<T>(endpoint, options, retryCount + 1);
      }
      throw err;
    }
  }

  get<T = any>(endpoint: string, options?: RequestInit & { params?: Record<string, any> }): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Upload file via multipart/form-data.
   * Omits Content-Type header so the browser sets it with the correct boundary.
   * Accepts either a FormData object or a plain object of string/Blob values.
   */
  async postForm<T = any>(endpoint: string, body: FormData | Record<string, string | Blob | boolean | number>): Promise<T> {
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // NOTE: Do NOT set Content-Type here — browser auto-sets multipart/form-data with boundary

    const formData = body instanceof FormData ? body : (() => {
      const fd = new FormData();
      for (const [key, value] of Object.entries(body)) {
        fd.append(key, value as string | Blob);
      }
      return fd;
    })();

    const url = `${this.baseUrl}${endpoint}`;
    let response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    // Handle 401 for form uploads too
    if (response.status === 401) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem('accessToken');
        const retryHeaders: Record<string, string> = {};
        if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          method: 'POST',
          headers: retryHeaders,
          body: formData,
        });
      }
    }

    return this.handleResponse(response);
  }

  put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL || ''
);


